import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Heading,
  Loader,
  Flex,
  Label,
  Text,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  type TableColumn,
} from '@vibe/core';
import { Check, Close, Add } from '@vibe/icons';
import {
  getLeaveRequests,
  getLeaveTypes,
  approveLeaveRequest,
  rejectLeaveRequest,
  revokeLeaveRequest,
  type LeaveRequest,
  type LeaveRequestStatus,
  type LeaveType,
} from '../../api/leave.api';
import { fetchMondayUsers, type MondayUser } from '../../api/monday.api';
import { getMyApprovees } from '../../api/settings.api';
import { formatDate } from '../../utils/dates';
import { useLeaveRequestStore, type StatusFilter } from '../../stores/leaveRequestStore';
import LeaveRequestDialog from './LeaveRequestDialog';

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'recalling', label: 'Recalling' },
  { key: 'recalled', label: 'Recalled' },
];

const STATUS_LABEL_COLOR: Record<
  LeaveRequestStatus,
  'EXPLOSIVE' | 'WORKING_ORANGE' | 'DONE_GREEN' | 'STUCK_RED' | 'DARK'
> = {
  pending: 'WORKING_ORANGE',
  approved: 'DONE_GREEN',
  rejected: 'STUCK_RED',
  recalling: 'EXPLOSIVE',
  recalled: 'DARK',
};

const TABLE_COLUMNS: TableColumn[] = [
  { id: 'employee', title: 'Employee', width: { min: 140, max: '1fr' } },
  { id: 'type', title: 'Type', width: { min: 80, max: '0.8fr' } },
  { id: 'dates', title: 'Dates', width: { min: 180, max: '1fr' } },
  { id: 'days', title: 'Days', width: 70 },
  { id: 'note', title: 'Note', width: { min: 60, max: '1.5fr' } },
  { id: 'created_by', title: 'Created by', width: { min: 100, max: '0.8fr' } },
  { id: 'status', title: 'Status', width: 110 },
  { id: 'actions', title: 'Actions', width: { min: 240, max: '1fr' } },
];

// ─── Props ────────────────────────────────────────────────────────────────────

interface ApprovalsTabProps {
  currentUserId: number | null;
  onPendingCountChange?: (count: number) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

const ApprovalsTab: React.FC<ApprovalsTabProps> = ({
  currentUserId,
  onPendingCountChange,
}) => {
  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [users, setUsers] = useState<Map<string, MondayUser>>(new Map());
  const [leaveTypes, setLeaveTypes] = useState<Map<string, LeaveType>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasApprovees, setHasApprovees] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);

  const statusFilter = useLeaveRequestStore((s) => s.approvalsStatus);
  const setStatusFilter = useLeaveRequestStore((s) => s.setApprovalsStatus);
  const page = useLeaveRequestStore((s) => s.approvalsPage);
  const setPage = useLeaveRequestStore((s) => s.setApprovalsPage);
  const pageSize = useLeaveRequestStore((s) => s.approvalsPageSize);
  const total = useLeaveRequestStore((s) => s.approvalsTotal);
  const setTotal = useLeaveRequestStore((s) => s.setApprovalsTotal);
  const setPendingApprovalCount = useLeaveRequestStore(
    (s) => s.setPendingApprovalCount,
  );

  const fetchRequests = useCallback(async () => {
    if (currentUserId == null) return;
    setIsLoading(true);
    setError(null);
    try {
      const params = {
        approverId: currentUserId,
        ...(statusFilter !== 'all' && { status: statusFilter }),
        page,
        pageSize,
      };
      const [result, allUsers, types, approvees] = await Promise.all([
        getLeaveRequests(params),
        fetchMondayUsers(),
        getLeaveTypes(),
        getMyApprovees(),
      ]);
      setRequests(result.data);
      setTotal(result.total);
      setHasApprovees(approvees.length > 0);

      // Update pending badge count when viewing pending filter
      if (statusFilter === 'pending') {
        setPendingApprovalCount(result.total);
        onPendingCountChange?.(result.total);
      }

      // Build lookup maps
      const userMap = new Map<string, MondayUser>();
      allUsers.forEach((u) => userMap.set(String(u.id), u));
      setUsers(userMap);
      const typeMap = new Map<string, LeaveType>();
      types.forEach((t) => typeMap.set(t.id, t));
      setLeaveTypes(typeMap);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load approval requests';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentUserId,
    statusFilter,
    page,
    pageSize,
    setTotal,
    setPendingApprovalCount,
    onPendingCountChange,
  ]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Resolve display names
  const getUserName = (mondayUserId: string): string =>
    users.get(mondayUserId)?.name ?? `User ${mondayUserId}`;

  const getLeaveTypeName = (leaveTypeId: string): string =>
    leaveTypes.get(leaveTypeId)?.name ?? 'Unknown';

  const handleAction = useCallback(
    async (action: 'approve' | 'reject' | 'revoke', id: string) => {
      try {
        switch (action) {
          case 'approve':
            await approveLeaveRequest(id);
            break;
          case 'reject':
            await rejectLeaveRequest(id);
            break;
          case 'revoke':
            await revokeLeaveRequest(id);
            break;
        }
        await fetchRequests();
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : `Failed to ${action} leave request`;
        setError(message);
      }
    },
    [fetchRequests],
  );

  const renderActions = useCallback(
    (req: LeaveRequest) => {
      return (
        <Flex gap={Flex.gaps.SMALL}>
          {/* Pending: approver can approve/reject */}
          {req.status === 'pending' && (
            <>
              <Button
                size={Button.sizes.SMALL}
                kind={Button.kinds.SECONDARY}
                color={Button.colors.POSITIVE}
                leftIcon={Check}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  handleAction('approve', req.id);
                }}
              >
                Approve
              </Button>
              <Button
                size={Button.sizes.SMALL}
                kind={Button.kinds.SECONDARY}
                color={Button.colors.NEGATIVE}
                leftIcon={Close}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  handleAction('reject', req.id);
                }}
              >
                Reject
              </Button>
            </>
          )}

          {/* Approved: approver can revoke */}
          {req.status === 'approved' && (
            <Button
              size={Button.sizes.SMALL}
              kind={Button.kinds.SECONDARY}
              color={Button.colors.NEGATIVE}
              leftIcon={Close}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                handleAction('revoke', req.id);
              }}
            >
              Revoke
            </Button>
          )}

          {/* Recalling: approver can approve the recall (revoke) */}
          {req.status === 'recalling' && (
            <Button
              size={Button.sizes.SMALL}
              kind={Button.kinds.SECONDARY}
              color={Button.colors.POSITIVE}
              leftIcon={Check}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                handleAction('revoke', req.id);
              }}
            >
              Approve Recall
            </Button>
          )}
        </Flex>
      );
    },
    [handleAction],
  );

  // Pagination
  const totalPages = Math.ceil(total / pageSize);
  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  const emptyState = (
    <div className="rp-leave-request-list__empty">
      <Text type={Text.types.TEXT1} style={{ color: '#676879' }}>
        {statusFilter === 'pending'
          ? 'No pending leave requests to approve.'
          : 'No leave requests found.'}
      </Text>
    </div>
  );

  const errorState = (
    <div className="rp-leave-request-list__error">
      <Text style={{ color: '#e2445c' }}>{error}</Text>
    </div>
  );

  return (
    <div className="rp-leave-request-list">
      {/* Header */}
      <Flex
        justify={Flex.justify.SPACE_BETWEEN}
        align={Flex.align.CENTER}
        className="rp-leave-request-list__header"
      >
        <Heading type={Heading.types.H3}>Approvals</Heading>
        {hasApprovees && (
          <Button
            size={Button.sizes.SMALL}
            kind={Button.kinds.SECONDARY}
            leftIcon={Add}
            onClick={() => setShowLeaveDialog(true)}
          >
            Create leave for team member
          </Button>
        )}
      </Flex>

      {/* Status filter */}
      <Flex
        gap={Flex.gaps.SMALL}
        className="rp-leave-request-list__filters"
      >
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.key}
            className={`rp-leave-request-list__filter-btn ${
              statusFilter === f.key
                ? 'rp-leave-request-list__filter-btn--active'
                : ''
            }`}
            onClick={() => setStatusFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </Flex>

      {/* Table */}
      {isLoading ? (
        <div className="rp-leave-request-list__loading">
          <Loader size={Loader.sizes.MEDIUM} />
        </div>
      ) : (
        <Table
          columns={TABLE_COLUMNS}
          errorState={errorState}
          emptyState={emptyState}
          dataState={{ isLoading, isError: !!error }}
        >
          <TableHeader>
            {TABLE_COLUMNS.map((col) => (
              <TableHeaderCell
                key={col.id}
                title={col.title}
                {...(col.id === 'created_by' ? {
                  infoContent: 'Shows who created the leave request. "—" means the employee requested it themselves. A name means it was created on their behalf by another user (e.g., a manager).',
                } : {})}
              />
            ))}
          </TableHeader>
          <TableBody>
            {requests.map((req) => (
              <TableRow key={req.id}>
                <TableCell>{getUserName(req.monday_user_id)}</TableCell>
                <TableCell>{getLeaveTypeName(req.leave_type_id)}</TableCell>
                <TableCell>
                  {formatDate(req.date_from, 'MMM d')} &ndash;{' '}
                  {formatDate(req.date_to, 'MMM d, yyyy')}
                </TableCell>
                <TableCell>
                  {req.total_days}
                  {req.capacity_type === 'half_day' ? ' (half)' : ''}
                </TableCell>
                <TableCell>
                  {req.note ? (
                    <Text
                      type={Text.types.TEXT2}
                      tooltipProps={{
                        content: req.note,
                        style: { zIndex: 9999 },
                      }}
                      style={{
                        color: 'var(--primary-text-color, #323338)',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        wordBreak: 'break-word',
                      }}
                    >
                      {req.note}
                    </Text>
                  ) : (
                    <Text type={Text.types.TEXT2} style={{ color: '#adadad' }}>
                      —
                    </Text>
                  )}
                </TableCell>
                <TableCell>
                  {req.created_by_user_id && req.created_by_user_id !== req.monday_user_id ? (
                    <Text type={Text.types.TEXT2} ellipsis style={{ color: 'var(--primary-text-color, #323338)' }}>
                      {users.get(req.created_by_user_id)?.name ?? `User #${req.created_by_user_id}`}
                    </Text>
                  ) : (
                    <Text type={Text.types.TEXT2} style={{ color: '#adadad' }}>—</Text>
                  )}
                </TableCell>
                <TableCell>
                  <Label
                    text={
                      req.status.charAt(0).toUpperCase() + req.status.slice(1)
                    }
                    color={Label.colors[STATUS_LABEL_COLOR[req.status]]}
                  />
                </TableCell>
                <TableCell>{renderActions(req)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Pagination */}
      {total > 0 && (
        <div className="rp-leave-request-list__pagination">
          <Text type={Text.types.TEXT2} color={Text.colors.SECONDARY}>
            {showingFrom}–{showingTo} of {total}
          </Text>
          <Flex gap={Flex.gaps.XS}>
            <Button
              size={Button.sizes.SMALL}
              kind={Button.kinds.TERTIARY}
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              Prev
            </Button>
            <Button
              size={Button.sizes.SMALL}
              kind={Button.kinds.TERTIARY}
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </Flex>
        </div>
      )}

      {/* Leave Request Dialog (create on behalf) */}
      {showLeaveDialog && currentUserId != null && (
        <LeaveRequestDialog
          currentUserId={currentUserId}
          onSubmitted={() => {
            setShowLeaveDialog(false);
            fetchRequests();
          }}
          onClose={() => setShowLeaveDialog(false)}
        />
      )}
    </div>
  );
};

export default ApprovalsTab;
